// src/features/channelManager/pages/AriGridPage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button, Card, Dropdown, message, Select, Space, Spin, Typography, Modal, Tag, Tooltip } from "antd";
import { DownOutlined, ReloadOutlined, SaveOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { channelsApi } from "./api/channelsApi";
import AriGrid from "./AriGrid";
import BulkUpdateDrawer from "./components/BulkUpdateDrawer";
import ChangesReviewDrawer from "./components/ChangesReviewDrawer";
import RatePlanDrawer from "./components/ari/RatePlanDrawer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const { Text } = Typography;

const DAYS_OPTIONS = [
  { value: 14, label: "14 days" },
  { value: 21, label: "21 days" },
  { value: 28, label: "28 days" },
];

const VIEW_OPTIONS = [
  { value: "rate_availability", label: "Rate & Availability" },
  { value: "only_availability", label: "Only Availability" },
  { value: "only_rate", label: "Only Rate" },
];

function safeYMD(d) {
  return dayjs(d).format("YYYY-MM-DD");
}

function buildDates(startDate, days) {
  const start = dayjs(startDate).startOf("day");
  return Array.from({ length: days }, (_, i) => start.add(i, "day").format("YYYY-MM-DD"));
}

export default function AriGridPage() {
  const params = useParams();
  const navigate = useNavigate();
  const propertyId = params.propertyId || params.id;

  const [loading, setLoading] = useState(true);
  const [gridLoading, setGridLoading] = useState(false);

  const [channelInfo, setChannelInfo] = useState(null);

  const [startDate, setStartDate] = useState(dayjs().startOf("day"));
  const [days, setDays] = useState(14);

  const [viewMode, setViewMode] = useState("rate_availability");
  const [roomFilter, setRoomFilter] = useState(null);
  const [rateFilter, setRateFilter] = useState(null);

  const [grid, setGrid] = useState(null);
  const [draft, setDraft] = useState({});
  const isDirty = useMemo(() => Object.keys(draft).length > 0, [draft]);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
const [ratePlanOpen, setRatePlanOpen] = useState(false);
const [ratePlanMode, setRatePlanMode] = useState("create");
const [editingRatePlan, setEditingRatePlan] = useState(null);
  // For ReactDate "open on click"
  const datePickerRef = useRef(null);

  const currency = useMemo(() => grid?.currency || channelInfo?.settings?.currency || "INR", [grid, channelInfo]);

  const rangeLabel = useMemo(() => {
    const s = dayjs(startDate);
    const e = s.add(days - 1, "day");
    return `${s.format("DD MMM YYYY")} → ${e.format("DD MMM YYYY")}`;
  }, [startDate, days]);

  const subTitleLabel = useMemo(() => {
    // no property UUID shown
    return channelInfo?.settings?.property_name || channelInfo?.settings?.hotel_name || "Inventory Control";
  }, [channelInfo]);

  const loadGrid = useCallback(async () => {
    if (!propertyId) {
      message.error("Missing propertyId in URL.");
      setLoading(false);
      return;
    }

    setGridLoading(true);
    try {
      // Header info (connected channel row)
      const listResp = await channelsApi.listCompanyChannels?.();
      const rows = listResp?.data?.data || [];
      const match = rows.find((r) => String(r.property_id) === String(propertyId));
      setChannelInfo(match || null);

      const resp = await channelsApi.ariGrid({
        property_id: propertyId,
        start_date: safeYMD(startDate),
        days,
      });

      const payload = resp.data?.data || resp.data || {};
      payload.start_date = payload.start_date || safeYMD(startDate);
      payload.days = payload.days || days;
      payload.currency = payload.currency || currency;
      payload.dates = Array.isArray(payload.dates) && payload.dates.length
  ? payload.dates
  : buildDates(payload.start_date, days);

      setGrid(payload);
      setDraft({});
    } catch (e) {
      console.error(e);
      message.error(e.response?.data?.message || "Failed to load grid");
      setGrid(null);
    } finally {
      setGridLoading(false);
      setLoading(false);
    }
  }, [propertyId, startDate, days, currency]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  const goToday = () => setStartDate(dayjs().startOf("day"));
  const goPrev = () => setStartDate((d) => dayjs(d).subtract(days, "day"));
  const goNext = () => setStartDate((d) => dayjs(d).add(days, "day"));

  const openCreateRatePlan = () => {
  setEditingRatePlan(null);
  setRatePlanMode("create");
  setRatePlanOpen(true);
};

const openEditRatePlan = (ratePlan) => {
  setEditingRatePlan(ratePlan || null);
  setRatePlanMode("edit");
  setRatePlanOpen(true);
};

const closeRatePlanDrawer = () => {
  setRatePlanOpen(false);
  setEditingRatePlan(null);
};

const handleRatePlanSaved = async () => {
  closeRatePlanDrawer();
  await loadGrid();
};
  const confirmReset = () => {
    if (!isDirty) return;
    Modal.confirm({
      title: "Discard unsaved changes?",
      content: "This will clear your local edits and restore values from the server.",
      okText: "Discard",
      okButtonProps: { danger: true },
      onOk: () => setDraft({}),
    });
  };

  async function saveChanges() {
    if (!propertyId || !isDirty) return;

    const availability = [];
    const rates = [];

    for (const [k, v] of Object.entries(draft)) {
      const parts = k.split("|");

      // A|<pms_room_type_id>|<YYYY-MM-DD>
      if (parts[0] === "A") {
        availability.push({
          pms_room_type_id: Number(parts[1]),
          date: parts[2],
          availability: Number(v),
        });
      }

      // R|<rate_plan_id>|<occ>|<YYYY-MM-DD>
      if (parts[0] === "R") {
        rates.push({
          rate_plan_id: parts[1],
          occupancy: Number(parts[2]),
          date: parts[3],
          amount: Number(v),
        });
      }
    }

    setGridLoading(true);
    try {
      await channelsApi.ariSave({
        property_id: propertyId,
        currency,
        push_to_channex: true,
        changes: { availability, rates },
      });

      message.success(`Saved ${availability.length + rates.length} changes.`);
      await loadGrid();
    } catch (e) {
      console.error(e);
      message.error(e.response?.data?.message || "Save failed");
      throw e;
    } finally {
      setGridLoading(false);
    }
  }

const actionItems = useMemo(() => {
  const items = [
    { key: "bulk", label: "Bulk Update" },
    { key: "create_rate_plan", label: "Create Rate Plan" },
  ];

  if (channelInfo?.code) {
    items.push({
      key: "settings",
      label: "Channel Settings",
      icon: <SettingOutlined />,
    });
  }

  return items;
}, [channelInfo]);

const handleActionsMenuClick = useCallback(
  ({ key }) => {
    if (key === "bulk") {
      setBulkOpen(true);
      return;
    }

    if (key === "create_rate_plan") {
      openCreateRatePlan();
      return;
    }

    if (key === "settings" && channelInfo?.code) {
      navigate(`/channel-manager/settings/${channelInfo.code}`);
    }
  },
  [channelInfo, navigate]
);
  const roomsForFilters = useMemo(() => {
    const rooms = grid?.rooms || [];
    return rooms.map((r) => ({ value: r.pms_room_type_id, label: r.name }));
  }, [grid]);

  const ratesForFilters = useMemo(() => {
    const rooms = grid?.rooms || [];
    const all = [];
    for (const r of rooms) {
      for (const rp of r.ratePlans || []) all.push({ value: rp.id, label: rp.title });
    }
    const seen = new Set();
    return all.filter((x) => (seen.has(x.value) ? false : (seen.add(x.value), true)));
  }, [grid]);

  const clearFilters = () => {
    setRoomFilter(null);
    setRateFilter(null);
  };

  if (loading && !grid) {
    return (
      <div className="page-wrapper-full">
        <div className="content container-fluid" style={{ paddingTop: 24 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        {/* PAGE HEADER */}
        <div className="page-header page-top-icons" style={{ marginBottom: 16 }}>
          <div className="row align-items-center">
            <div className="col-md-4">
              <div>
                <h3 className="page-title" style={{ marginBottom: 0 }}>
                  Rates & Availability
                </h3>

                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                  <Text type="secondary" style={{ fontWeight: 600 }}>
                    {subTitleLabel}
                  </Text>

                  <Tag style={{ marginInlineStart: 0 }}>{currency}</Tag>
                  <Tag color="blue" style={{ marginInlineStart: 0 }}>
                    {rangeLabel}
                  </Tag>

                  {isDirty && (
                    <Tag color="gold" style={{ marginInlineStart: 0 }}>
                      Unsaved changes
                    </Tag>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-8">
              {/* TOOLBAR */}
              <div className="d-flex justify-content-end align-items-center flex-wrap gap-2">
                <Select value={viewMode} onChange={setViewMode} style={{ width: 200 }} options={VIEW_OPTIONS} />

                <Select
                  allowClear
                  value={roomFilter}
                  onChange={setRoomFilter}
                  placeholder="Rooms"
                  style={{ width: 200 }}
                  options={roomsForFilters}
                />

                <Select
                  allowClear
                  value={rateFilter}
                  onChange={setRateFilter}
                  placeholder="Rates"
                  style={{ width: 230 }}
                  options={ratesForFilters}
                />

                <Button onClick={clearFilters} style={{ borderRadius: 10 }}>
                  Clear
                </Button>

                <Select value={days} onChange={setDays} style={{ width: 125 }} options={DAYS_OPTIONS} />



                {/* hidden react-datepicker input (opens from button) */}
                <div style={{ position: "absolute", left: -9999, width: 1, height: 1, overflow: "hidden" }}>
                  <DatePicker
                    ref={datePickerRef}
                    selected={startDate.toDate()}
                    onChange={(d) => d && setStartDate(dayjs(d).startOf("day"))}
                    dateFormat="yyyy-MM-dd"
                    className="reactDateInput"
                  />
                </div>

                <Button onClick={goToday} style={{ borderRadius: 10 }}>
                  Today
                </Button>

                <Button icon={<ReloadOutlined />} loading={gridLoading} onClick={loadGrid} style={{ borderRadius: 10 }}>
                  Refresh
                </Button>

                <Tooltip title={!isDirty ? "Make changes in the grid to enable saving" : ""}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    disabled={!isDirty}
                    onClick={() => setReviewOpen(true)}
                    style={{ borderRadius: 10 }}
                  >
                    Save
                  </Button>
                </Tooltip>

                <Button disabled={!isDirty} onClick={confirmReset} style={{ borderRadius: 10 }}>
                  Reset
                </Button>
<Dropdown menu={{ items: actionItems, onClick: handleActionsMenuClick }} trigger={["click"]}>
  <Button style={{ borderRadius: 10 }}>
    Actions <DownOutlined />
  </Button>
</Dropdown>
              </div>

              {/* Slim dirty strip */}
              {isDirty && (
                <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      padding: "8px 10px",
                      borderRadius: 12,
                      background: "rgba(245,158,11,0.10)",
                      border: "1px solid rgba(245,158,11,0.25)",
                    }}
                  >
                    <Text style={{ fontWeight: 700 }}>Review before saving</Text>
                    <Space>
                      <Button size="small" onClick={() => setReviewOpen(true)}>
                        Review
                      </Button>
                      <Button size="small" danger onClick={confirmReset}>
                        Discard
                      </Button>
                    </Space>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* GRID */}
        <Card style={{ borderRadius: 16 }}>
          {gridLoading && !grid ? (
            <Spin />
          ) : grid?.rooms?.length ? (
           <AriGrid
  grid={grid}
  draft={draft}
  setDraft={setDraft}
  viewMode={viewMode}
  roomFilter={roomFilter}
  rateFilter={rateFilter}
  startDateLabel={dayjs(startDate).format("DD MMM YYYY")}
  onPrev={goPrev}
  onNext={goNext}
  onPickStartDate={() => datePickerRef.current?.setOpen?.(true)}
  onEditRatePlan={openEditRatePlan}
/>
          ) : (
            <div style={{ padding: 24 }}>
              <Text type="secondary">No rooms/rate plans found. Complete provisioning + mapping first.</Text>
            </div>
          )}
        </Card>

        {/* Bulk update */}
        <BulkUpdateDrawer
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          propertyId={propertyId}
          rooms={grid?.rooms || []}
          currency={currency}
          api={channelsApi}
          onApplied={loadGrid}
        />
<RatePlanDrawer
  open={ratePlanOpen}
  mode={ratePlanMode}
  propertyId={propertyId}
  rooms={grid?.rooms || []}
  currency={currency}
  initialData={editingRatePlan}
  api={channelsApi}
  onClose={closeRatePlanDrawer}
  onSaved={handleRatePlanSaved}
/>
        {/* Review & Save */}
        <ChangesReviewDrawer
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          draft={draft}
          grid={grid}
          currency={currency}
          onConfirmSave={saveChanges}
        />
      </div>

      <style>{`
        .reactDateInput{
          border: none !important;
          outline: none !important;
          font-weight: 800;
          color: #0f172a;
          width: 135px;
          background: transparent;
        }
        .reactDateInput::placeholder{ color: rgba(15,23,42,0.45); }
      `}</style>
    </div>
  );
}
